/**
 * Assets Management Page
 * View and manage property assets and maintenance records
 */
// @ts-nocheck

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Progress,
  useColorModeValue,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdRefresh,
  MdMoreVert,
  MdVisibility,
  MdBuild,
  MdWarning,
  MdCheckCircle,
  MdAdd,
  MdHistory,
  MdTrendingDown,
  MdAttachMoney,
} from 'react-icons/md';
import { Asset, AssetStats, AssetFilters, MaintenanceRecord } from '../../types/asset';
import { assetService } from '../services/assetService';

export function AssetsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isMaintenanceOpen, onOpen: onMaintenanceOpen, onClose: onMaintenanceClose } = useDisclosure();
  
  const [assets, setAssets] = useState<Asset[]>([]);
  const [stats, setStats] = useState<AssetStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [filters, setFilters] = useState<AssetFilters>({
    search: '',
    category: 'all',
    status: 'all',
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    loadAssets();
  }, [filters]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const response = await assetService.getAssets(filters);
      setAssets(response.assets);
      
      // Load stats separately
      const statsResponse = await assetService.getAssetStats();
      setStats(statsResponse.stats);
    } catch (error: any) {
      console.error('Error loading assets:', error);
      toast({
        title: 'Error loading assets',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Fallback to empty data on error
      setAssets([]);
      setStats({ total: 0, byCategory: {}, byCondition: {}, maintenanceDue: 0, costToReplace: 0 });
    } finally {
      setLoading(false);
    }
  };

  const loadMaintenanceHistory = async (assetId: string) => {
    try {
      // TODO: Get landlordId from auth context
      const landlordId = 'current-landlord';
      const response = await assetService.getMaintenanceHistory(landlordId, assetId);
      setMaintenanceRecords(response.records);
    } catch (error: any) {
      console.error('Error loading maintenance history:', error);
      toast({
        title: 'Error loading maintenance history',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Fallback to empty array on error
      setMaintenanceRecords([]);
    }
  };

  const viewMaintenanceHistory = async (asset: Asset) => {
    setSelectedAsset(asset);
    // TODO: Load actual maintenance records
    setMaintenanceRecords(asset.maintenanceRecords || []);
    onMaintenanceOpen();
  };

  const getCategoryColor = (category: Asset['category']) => {
    const colors: Record<Asset['category'], string> = {
      appliance: 'blue',
      hvac: 'orange',
      plumbing: 'cyan',
      electrical: 'yellow',
      structural: 'red',
      landscaping: 'green',
      other: 'gray',
    };
    return colors[category] || 'gray';
  };

  const getStatusColor = (status: Asset['status']) => {
    switch (status) {
      case 'active': return 'green';
      case 'maintenance': return 'yellow';
      case 'retired': return 'gray';
      case 'disposed': return 'red';
      default: return 'gray';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const calculateDepreciation = (asset: Asset) => {
    const purchaseValue = asset.purchasePrice;
    const currentValue = asset.currentValue;
    if (purchaseValue === 0) return 0;
    return ((purchaseValue - currentValue) / purchaseValue) * 100;
  };

  const isMaintenanceDue = (asset: Asset) => {
    if (!asset.nextMaintenanceDate) return false;
    return new Date(asset.nextMaintenanceDate) <= new Date();
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={0}>
          <Heading size="lg">Asset Management</Heading>
          <Text color="gray.600" fontSize="sm">
            Track and manage property assets and maintenance
          </Text>
        </VStack>
        <HStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={loadAssets}
            variant="outline"
            isLoading={loading}
          >
            Refresh
          </Button>
          <Button colorScheme="blue" leftIcon={<MdAdd />}>
            Add Asset
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      {stats && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdBuild} boxSize={8} color="blue.500" />
                  <Box>
                    <StatLabel>Total Assets</StatLabel>
                    <StatNumber>{stats.totalAssets}</StatNumber>
                    <StatHelpText>{stats.byStatus.active} active</StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdAttachMoney} boxSize={8} color="green.500" />
                  <Box>
                    <StatLabel>Total Value</StatLabel>
                    <StatNumber fontSize="lg">{formatCurrency(stats.totalValue)}</StatNumber>
                    <StatHelpText>Current valuation</StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdTrendingDown} boxSize={8} color="orange.500" />
                  <Box>
                    <StatLabel>Depreciation</StatLabel>
                    <StatNumber fontSize="lg">{formatCurrency(stats.depreciation)}</StatNumber>
                    <StatHelpText>
                      <StatArrow type="decrease" />
                      From purchase
                    </StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdWarning} boxSize={8} color="red.500" />
                  <Box>
                    <StatLabel>Maintenance Due</StatLabel>
                    <StatNumber>{stats.maintenanceDue}</StatNumber>
                    <StatHelpText>Requires attention</StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Category Distribution */}
      {stats && (
        <Card bg={cardBg} borderColor={borderColor} mb={6}>
          <CardBody>
            <Heading size="sm" mb={4}>Assets by Category</Heading>
            <SimpleGrid columns={{ base: 2, md: 4, lg: 7 }} spacing={4}>
              {Object.entries(stats.byCategory).map(([category, count]) => (
                <HStack key={category}>
                  <Badge colorScheme={getCategoryColor(category as Asset['category'])} fontSize="sm" px={2}>
                    {category}
                  </Badge>
                  <Text fontWeight="bold">{String(count)}</Text>
                </HStack>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Filters */}
      <Card bg={cardBg} borderColor={borderColor} mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <InputGroup>
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search assets..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </InputGroup>

            <Select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value as any })}
            >
              <option value="all">All Categories</option>
              <option value="appliance">Appliances</option>
              <option value="hvac">HVAC</option>
              <option value="plumbing">Plumbing</option>
              <option value="electrical">Electrical</option>
              <option value="structural">Structural</option>
              <option value="landscaping">Landscaping</option>
              <option value="other">Other</option>
            </Select>

            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="maintenance">In Maintenance</option>
              <option value="retired">Retired</option>
              <option value="disposed">Disposed</option>
            </Select>

            <Select
              placeholder="All Properties"
              onChange={(e) => setFilters({ ...filters, propertyId: e.target.value || undefined })}
            >
              <option value="">All Properties</option>
              {/* TODO: Load property options */}
            </Select>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Assets Table */}
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">Assets</Heading>
            <Text fontSize="sm" color="gray.600">
              {assets.length} assets
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : assets.length === 0 ? (
            <Flex justify="center" align="center" h="200px" direction="column">
              <Icon as={MdBuild} boxSize={12} color="gray.400" mb={2} />
              <Text color="gray.600">No assets found</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Asset</Th>
                    <Th>Property</Th>
                    <Th>Category</Th>
                    <Th>Purchase Info</Th>
                    <Th>Current Value</Th>
                    <Th>Depreciation</Th>
                    <Th>Maintenance</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {assets.map((asset) => {
                    const depreciationPercent = calculateDepreciation(asset);
                    const maintenanceDue = isMaintenanceDue(asset);
                    
                    return (
                      <Tr
                        key={asset.id}
                        _hover={{ bg: hoverBg }}
                        cursor="pointer"
                        onClick={() => navigate(`/admin/assets/${asset.id}`)}
                      >
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{asset.name}</Text>
                            {asset.serialNumber && (
                              <Text fontSize="xs" color="gray.600">SN: {asset.serialNumber}</Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm">{asset.propertyName}</Text>
                            <Text fontSize="xs" color="gray.600">{asset.landlordName}</Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={getCategoryColor(asset.category)}>
                            {asset.category}
                          </Badge>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{formatCurrency(asset.purchasePrice)}</Text>
                            <Text fontSize="xs" color="gray.600">
                              {formatDate(asset.purchaseDate)}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{formatCurrency(asset.currentValue)}</Text>
                            {asset.warrantyExpiry && new Date(asset.warrantyExpiry) > new Date() && (
                              <Badge colorScheme="green" fontSize="xs">Warranty</Badge>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text 
                              fontWeight="bold" 
                              color={depreciationPercent > 70 ? 'red.500' : depreciationPercent > 40 ? 'orange.500' : 'green.500'}
                            >
                              {depreciationPercent.toFixed(1)}%
                            </Text>
                            <Progress
                              value={depreciationPercent}
                              size="sm"
                              colorScheme={depreciationPercent > 70 ? 'red' : depreciationPercent > 40 ? 'orange' : 'green'}
                              w="60px"
                              borderRadius="full"
                            />
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            {maintenanceDue ? (
                              <>
                                <Badge colorScheme="red" fontSize="xs">
                                  <HStack spacing={1}>
                                    <Icon as={MdWarning} />
                                    <Text>Overdue</Text>
                                  </HStack>
                                </Badge>
                                {asset.nextMaintenanceDate && (
                                  <Text fontSize="xs" color="red.600">
                                    {formatDate(asset.nextMaintenanceDate)}
                                  </Text>
                                )}
                              </>
                            ) : asset.nextMaintenanceDate ? (
                              <>
                                <Badge colorScheme="green" fontSize="xs">Scheduled</Badge>
                                <Text fontSize="xs" color="gray.600">
                                  {formatDate(asset.nextMaintenanceDate)}
                                </Text>
                              </>
                            ) : (
                              <Text fontSize="xs" color="gray.500">Not scheduled</Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(asset.status)}>
                            {asset.status}
                          </Badge>
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<MdMoreVert />}
                              variant="ghost"
                              size="sm"
                            />
                            <MenuList>
                              <MenuItem
                                icon={<MdVisibility />}
                                onClick={() => navigate(`/admin/assets/${asset.id}`)}
                              >
                                View Details
                              </MenuItem>
                              <MenuItem
                                icon={<MdHistory />}
                                onClick={() => viewMaintenanceHistory(asset)}
                              >
                                Maintenance History
                              </MenuItem>
                              <MenuItem icon={<MdBuild />}>
                                Schedule Maintenance
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Maintenance History Modal */}
      <Modal isOpen={isMaintenanceOpen} onClose={onMaintenanceClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Maintenance History - {selectedAsset?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {maintenanceRecords.length === 0 ? (
              <Flex justify="center" align="center" h="100px" direction="column">
                <Icon as={MdHistory} boxSize={10} color="gray.400" mb={2} />
                <Text color="gray.600">No maintenance records</Text>
              </Flex>
            ) : (
              <VStack spacing={4} align="stretch">
                {maintenanceRecords.map((record) => (
                  <Card key={record.id} variant="outline">
                    <CardBody>
                      <HStack justify="space-between" mb={2}>
                        <Badge colorScheme={
                          record.type === 'routine' ? 'blue' :
                          record.type === 'repair' ? 'orange' :
                          record.type === 'inspection' ? 'green' : 'red'
                        }>
                          {record.type}
                        </Badge>
                        <Text fontSize="sm" color="gray.600">
                          {formatDate(record.date)}
                        </Text>
                      </HStack>
                      <Text mb={2}>{record.description}</Text>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                          Performed by: {record.performedBy}
                        </Text>
                        <Text fontWeight="bold">{formatCurrency(record.cost)}</Text>
                      </HStack>
                      {record.nextMaintenanceDate && (
                        <Text fontSize="sm" color="blue.600" mt={2}>
                          Next maintenance: {formatDate(record.nextMaintenanceDate)}
                        </Text>
                      )}
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

// Mock Data
const mockAssets: Asset[] = [
  {
    id: '1',
    landlordId: 'LL001',
    landlordName: 'John Smith',
    propertyId: 'P001',
    propertyName: 'Maple Grove Apartments',
    category: 'hvac',
    name: 'Central HVAC System - Building A',
    description: 'Carrier 5-ton commercial HVAC unit',
    serialNumber: 'HVAC-2024-001',
    purchaseDate: new Date('2020-03-15'),
    purchasePrice: 15000,
    currentValue: 9000,
    warrantyExpiry: new Date('2026-03-15'),
    lifeExpectancy: 15,
    status: 'active',
    maintenanceRecords: [
      {
        id: 'M1',
        assetId: '1',
        date: new Date('2025-09-01'),
        type: 'routine',
        description: 'Quarterly filter replacement and system check',
        cost: 250,
        performedBy: 'ABC HVAC Services',
        nextMaintenanceDate: new Date('2025-12-01'),
      },
    ],
    lastMaintenanceDate: new Date('2025-09-01'),
    nextMaintenanceDate: new Date('2025-12-01'),
    createdAt: new Date('2020-03-15'),
    updatedAt: new Date('2025-11-20'),
  },
  {
    id: '2',
    landlordId: 'LL001',
    landlordName: 'John Smith',
    propertyId: 'P001',
    propertyName: 'Maple Grove Apartments',
    category: 'appliance',
    name: 'Commercial Washer - Unit 5',
    description: 'Speed Queen commercial washing machine',
    serialNumber: 'SQ-2022-789',
    purchaseDate: new Date('2022-06-10'),
    purchasePrice: 3500,
    currentValue: 2450,
    warrantyExpiry: new Date('2025-06-10'),
    lifeExpectancy: 10,
    status: 'active',
    maintenanceRecords: [],
    nextMaintenanceDate: new Date('2025-11-20'),
    createdAt: new Date('2022-06-10'),
    updatedAt: new Date('2025-11-15'),
  },
];

const mockStats: AssetStats = {
  totalAssets: 2,
  totalValue: 11450,
  totalPurchaseValue: 18500,
  depreciation: 7050,
  maintenanceDue: 1,
  byCategory: {
    hvac: 1,
    appliance: 1,
    plumbing: 0,
    electrical: 0,
    structural: 0,
    landscaping: 0,
    other: 0,
  },
  byStatus: {
    active: 2,
    maintenance: 0,
    retired: 0,
    disposed: 0,
  },
};
